import { Button, PropertyPanel, PropertyPanelSection, Text } from "@showitinc/showit-wc";
import React, { useCallback, useEffect, useState } from "react";
import { PropNames } from "@showitinc/merge-utils";
import {
  buildFormFieldPropData,
  FORM_FIELD_INPUT_GROUPS,
  FORM_FIELD_INPUT_PROPS,
  FORM_FIELD_LABEL_PROPS,
  FORM_FIELD_NESTED_INPUT_PROPS,
  FORM_FIELD_PROPERTY_META_MAP,
  FORM_FIELD_THEME_CHECKBOX_GROUPS,
  FORM_FIELD_THEME_CHECKBOX_META_MAP,
  FORM_FIELD_THEME_CHECKBOX_PROPS,
  FORM_FIELD_THEME_DROPDOWN_GROUPS,
  FORM_FIELD_THEME_DROPDOWN_PROPS,
  FORM_FIELD_THEME_RADIO_GROUPS,
  FORM_FIELD_THEME_RADIO_PROPS,
  FORM_STATE_OPTIONS,
  filterApplicableFormFieldProps,
  getFormFieldStateProps,
  withInheritedSync,
} from "@/utils/Element/FormFieldPropertyMaps";
import { getRemoveCustomizationUpdates, isStatePropCustomized } from "@/utils/FormFieldStateUtils";
import { resolveFormFieldValue } from "@/utils/FormFieldStyleResolver";
import UIActions from "../../actions/UIActions";
import DynamicInput from "../../app2components/base/DynamicInput";
import AppConstants from "../../constants/AppConstants";
import {
  FORM_STATE_DEFAULT,
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_RADIOS,
} from "@/constants/FormFieldConstants.mjs";
import useStyleSync from "../../hooks/useStyleSync";
import ActiveBlockContainerStore from "../../stores/ActiveBlockContainerStore.mjs";
import { ensureArray } from "../../utils/JSUtils";
import { applyPropInfoToTheme } from "../../utils/SyncUtils";
import styles from "./DesignSettingsFormFields.module.css";
import { buildStateSectionGroups } from "./DesignSettingsFormFields.utils";
import DesignSettingsCustomizeMenu from "./DesignSettingsCustomizeMenu";
import DesignSettingsFormFieldsPreview from "./DesignSettingsFormFieldsPreview";

const { LABEL_POSITION } = PropNames;

const {
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  THEME_STYLES,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_CHECKBOX,
} = AppConstants;

const SECTION_LABEL = "label";
const SECTION_INPUT_FIELD = "inputField";
const SECTION_DROPDOWN = "dropdown";
const SECTION_RADIO = "radio";
const SECTION_CHECKBOX = "checkbox";

const SECTIONS = [
  { key: SECTION_LABEL, title: "Label", props: FORM_FIELD_LABEL_PROPS },
  { key: SECTION_INPUT_FIELD, title: "Input Field", props: FORM_FIELD_INPUT_PROPS },
  { key: SECTION_DROPDOWN, title: "Dropdown", props: FORM_FIELD_THEME_DROPDOWN_PROPS },
  { key: SECTION_RADIO, title: "Radio Buttons", props: FORM_FIELD_THEME_RADIO_PROPS },
  { key: SECTION_CHECKBOX, title: "Checkbox", props: FORM_FIELD_THEME_CHECKBOX_PROPS },
];

const SECTION_BY_TYPE = {
  [ELEMENT_TYPE_FORM_INPUT]: SECTION_INPUT_FIELD,
  [ELEMENT_TYPE_FORM_SELECT]: SECTION_DROPDOWN,
  [ELEMENT_TYPE_FORM_RADIO]: SECTION_RADIO,
  [ELEMENT_TYPE_FORM_CHECKBOX]: SECTION_CHECKBOX,
};

/** The first entry is the default. */
const PREVIEW_BACKGROUNDS = [
  { value: "checker", label: "Checker" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const SECTION_GROUPS = {
  [SECTION_INPUT_FIELD]: FORM_FIELD_INPUT_GROUPS,
  [SECTION_DROPDOWN]: FORM_FIELD_THEME_DROPDOWN_GROUPS,
  [SECTION_RADIO]: FORM_FIELD_THEME_RADIO_GROUPS,
  [SECTION_CHECKBOX]: FORM_FIELD_THEME_CHECKBOX_GROUPS,
};

/**
 * A row of mutually exclusive tab buttons with dividers between them.
 * @param {Object} props
 * @param {{ value: string, label: string }[]} props.options
 * @param {string} props.value - The active option's value.
 * @param {Function} props.onChange - Called with an option's value when it is clicked.
 * @param {string} props.idPrefix - Prefixes each button's `data-testid` and `data-e2e`.
 * @param {string} [props.className]
 * @param {Function} [props.onClick] - Click handler on the row itself.
 * @returns {React.ReactElement}
 */
const SegmentedTabs = ({ options, value, onChange, idPrefix, className = "", onClick }) => (
  <div className={`${styles["state-tabs"]} ${className}`} onClick={onClick}>
    {options.map((option, index) => (
      <React.Fragment key={option.value}>
        {index > 0 && <div className={styles["state-tab-divider"]} />}
        <button
          type="button"
          className={styles["state-tab"]}
          data-active={value === option.value}
          data-e2e={`${idPrefix}${option.value}`}
          data-testid={`${idPrefix}${option.value}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      </React.Fragment>
    ))}
  </div>
);

/**
 * Form field design settings panel.
 * @param {Object} props
 * @param {import("immutable").Map} props.theme - Theme being edited.
 * @param {Function} props.setTheme - Theme update function.
 * @param {number} props.panelHeight - Panel height in pixels.
 * @param {Object} props.designContext - Design context for the preview's font tags.
 * @returns {React.ReactElement}
 */
const DesignSettingsFormFields = ({ theme, setTheme, panelHeight, designContext }) => {
  const [fieldState, setFieldState] = useState(FORM_STATE_DEFAULT);
  const [selectedSection, setSelectedSection] = useState(SECTION_LABEL);
  const [previewBackground, setPreviewBackground] = useState(PREVIEW_BACKGROUNDS[0].value);
  // State props added from a section's Customize menu but not stored yet, keyed by section.
  const [pendingCustomizations, setPendingCustomizations] = useState({});
  const _selectFieldSection = useCallback((type) => setSelectedSection(SECTION_BY_TYPE[type]), []);

  // Reset restores the theme, so rows added from Customize go with it.
  useEffect(() => {
    const resetHandler = () => setPendingCustomizations({});
    document.addEventListener("resetThemePanel", resetHandler);
    return () => document.removeEventListener("resetThemePanel", resetHandler);
  }, []);

  const selectedLayout = ActiveBlockContainerStore.getSelectedLayout();

  // Label and Input Field edit the `field` entry, which every other form style inherits from.
  const fieldEntry = theme.getIn([THEME_STYLES, FORM_STYLE_FIELD]);
  const dropdownEntry = theme.getIn([THEME_STYLES, FORM_STYLE_DROPDOWN]);
  const radiosEntry = theme.getIn([THEME_STYLES, FORM_STYLE_RADIOS]);
  const checkboxesEntry = theme.getIn([THEME_STYLES, FORM_STYLE_CHECKBOXES]);
  const labelProps = getFormFieldStateProps(FORM_FIELD_LABEL_PROPS, fieldState);
  const inputProps = getFormFieldStateProps(FORM_FIELD_INPUT_PROPS, fieldState);
  const dropdownProps = getFormFieldStateProps(FORM_FIELD_THEME_DROPDOWN_PROPS, fieldState);
  const radioProps = getFormFieldStateProps(FORM_FIELD_THEME_RADIO_PROPS, fieldState);
  const checkboxProps = getFormFieldStateProps(FORM_FIELD_THEME_CHECKBOX_PROPS, fieldState);
  const palette = theme.get("colors")?.toJS();

  const _onPropChange = (propInfo) =>
    setTheme(ensureArray(propInfo).reduce(applyPropInfoToTheme, theme));

  const fieldSource = {
    element: fieldEntry,
    styleSync: useStyleSync({
      parentType: THEME_STYLES,
      childType: FORM_STYLE_FIELD,
      styles: fieldEntry,
      selectedLayout,
      onChange: _onPropChange,
      propKeys: [...labelProps, ...inputProps],
    }),
  };

  const dropdownSource = {
    element: dropdownEntry,
    styleSync: useStyleSync({
      parentType: THEME_STYLES,
      childType: FORM_STYLE_DROPDOWN,
      styles: dropdownEntry,
      selectedLayout,
      onChange: _onPropChange,
      propKeys: dropdownProps,
    }),
  };

  const radioSource = {
    element: radiosEntry,
    styleSync: useStyleSync({
      parentType: THEME_STYLES,
      childType: FORM_STYLE_RADIOS,
      styles: radiosEntry,
      selectedLayout,
      onChange: _onPropChange,
      propKeys: radioProps,
    }),
  };

  const checkboxSource = {
    element: checkboxesEntry,
    metaMap: FORM_FIELD_THEME_CHECKBOX_META_MAP,
    styleSync: useStyleSync({
      parentType: THEME_STYLES,
      childType: FORM_STYLE_CHECKBOXES,
      styles: checkboxesEntry,
      selectedLayout,
      onChange: _onPropChange,
      propKeys: checkboxProps,
    }),
  };

  /**
   * Wraps a source's styleSync so inherited props split, merge and sync correctly.
   * @param {{ element: import("immutable").Map, styleSync: Object }} source - A section source.
   * @returns {Object} The wrapped styleSync.
   */
  const _inheritedSync = ({ element, styleSync }) =>
    withInheritedSync(styleSync, {
      element,
      theme,
      layout: selectedLayout,
      onChange: _onPropChange,
    });

  /**
   * Builds the propData one control renders from.
   * @param {string} prop - Prop code, state-prefixed on Focus and Error.
   * @param {Object} source - The section source whose entry the control edits.
   * @returns {Object} propData
   */
  const _buildProp = (prop, source) =>
    buildFormFieldPropData({
      element: source.element,
      theme,
      layout: selectedLayout,
      prop,
      styleSync: _inheritedSync(source),
      palette,
      metaMap: source.metaMap,
    });

  /** The entry each section edits. */
  const sectionSources = {
    [SECTION_LABEL]: fieldSource,
    [SECTION_INPUT_FIELD]: fieldSource,
    [SECTION_DROPDOWN]: dropdownSource,
    [SECTION_RADIO]: radioSource,
    [SECTION_CHECKBOX]: checkboxSource,
  };

  /**
   * @param {string} key - Section key.
   * @returns {string[]} The section's state props added from Customize but not stored yet.
   */
  const _sectionPending = (key) => pendingCustomizations[key] ?? [];

  /**
   * Updates one section's pending list.
   * @param {string} key - Section key.
   * @param {(pending: string[]) => string[]} update - Maps the current list to the next one.
   * @returns {void}
   */
  const _setSectionPending = (key, update) =>
    setPendingCustomizations((prev) => ({ ...prev, [key]: update(prev[key] ?? []) }));

  /**
   * Shows a state prop as a row. Nothing is written: the row shows the inherited value, and the
   * first edit takes its sync from Default.
   * @param {string} key - Section key.
   * @param {string} stateProp - State-prefixed prop code, e.g. `focus.control.text.font`.
   * @returns {void}
   */
  const _addCustomization = (key, stateProp) =>
    _setSectionPending(key, (pending) => [...pending, stateProp]);

  /**
   * Hides a state prop's row and deletes what the entry stores for it, so it reads Default again.
   * A prop added but not yet stored only leaves the pending list.
   * @param {string} key - Section key.
   * @param {string} stateProp - State-prefixed prop code.
   * @returns {void}
   */
  const _removeCustomization = (key, stateProp) => {
    const { element, metaMap = FORM_FIELD_PROPERTY_META_MAP } = sectionSources[key];
    if (_sectionPending(key).includes(stateProp)) {
      _setSectionPending(key, (pending) => pending.filter((name) => name !== stateProp));
      if (!isStatePropCustomized(element, selectedLayout, stateProp, metaMap)) return;
    }
    _onPropChange(getRemoveCustomizationUpdates({ entry: element, stateProp, metaMap }));
  };

  /**
   * Lays out a section for the selected state. Default shows every control. Focus and Error show
   * the customized ones, each with a Remove Customization entry, and offer the rest on the menu.
   * Either way a group with nothing to show is dropped.
   * @returns {{ groups: { title?: string, controls: { propData: Object, nested: boolean }[] }[],
   *   menuItems: { stateProp: string, label: string }[] }}
   */
  const _sectionGroups = ({ key, props }) => {
    const source = sectionSources[key];
    const labelPosition = resolveFormFieldValue(
      source.element,
      selectedLayout,
      LABEL_POSITION,
      theme,
    );
    const groups = (SECTION_GROUPS[key] ?? [{ props }]).map(({ title, props: groupProps }) => ({
      title,
      props: filterApplicableFormFieldProps(groupProps, { labelPosition }),
    }));

    if (fieldState === FORM_STATE_DEFAULT) {
      return {
        groups: groups
          .map(({ title, props: groupProps }) => ({
            title,
            controls: groupProps.map((name) => ({
              propData: _buildProp(name, source),
              nested: FORM_FIELD_NESTED_INPUT_PROPS.includes(name),
            })),
          }))
          .filter(({ controls }) => controls.length),
        menuItems: [],
      };
    }

    const { groups: stateGroups, menuItems } = buildStateSectionGroups({
      groups,
      state: fieldState,
      entry: source.element,
      layout: selectedLayout,
      pending: _sectionPending(key),
      metaMap: source.metaMap ?? FORM_FIELD_PROPERTY_META_MAP,
    });

    return {
      groups: stateGroups.map(({ title, rows }) => ({
        title,
        controls: rows.map(({ stateProp, nested }) => ({
          propData: {
            ..._buildProp(stateProp, source),
            removeCustomization: () => _removeCustomization(key, stateProp),
          },
          nested,
        })),
      })),
      menuItems,
    };
  };

  const _selectActiveLayout = (layout) => UIActions.selectActiveLayout({ layout, from: "panel" });

  return (
    <div className={styles.root} style={{ maxHeight: panelHeight }}>
      <PropertyPanel className={styles["property-panel"]}>
        <div className={styles["section-container"]}>
          <div className={styles["state-tabset"]}>
            <SegmentedTabs
              options={FORM_STATE_OPTIONS}
              value={fieldState}
              onChange={setFieldState}
              idPrefix="form-field-tab-"
            />
          </div>

          {SECTIONS.map(({ key, title, props }) => {
            const { groups, menuItems } = _sectionGroups({ key, props });

            return (
              <div
                key={key}
                className={styles["section-container"]}
                data-open={selectedSection === key}
              >
                <PropertyPanelSection
                  title={title}
                  open={selectedSection === key}
                  onClick={() => setSelectedSection(key)}
                  data-e2e={`form-fields-section-${key}`}
                  data-testid={`section-${key}`}
                >
                  {groups.map(({ title: groupTitle, controls }) => (
                    <React.Fragment key={groupTitle ?? key}>
                      {groupTitle && <Text className="panel-subheading">{groupTitle}</Text>}
                      {controls.map(({ propData, nested }) => (
                        <div
                          key={propData.name}
                          className={nested ? styles["nested-prop"] : undefined}
                          data-testid={`form-field-control-${propData.name}`}
                        >
                          <DynamicInput propData={propData} layout={selectedLayout} shift="right" />
                        </div>
                      ))}
                    </React.Fragment>
                  ))}

                  <DesignSettingsCustomizeMenu
                    items={menuItems.map(({ stateProp, label }) => ({ value: stateProp, label }))}
                    onSelect={(stateProp) => _addCustomization(key, stateProp)}
                    testId={`form-field-customize-${key}`}
                    itemTestIdPrefix="form-field-customize-item-"
                  />
                </PropertyPanelSection>
              </div>
            );
          })}
        </div>
      </PropertyPanel>

      <div className={styles.previewContainer}>
        {[LAYOUT_MOBILE, LAYOUT_DESKTOP].map((layout) => {
          const isMobile = layout === LAYOUT_MOBILE;
          const isActive = selectedLayout === layout;

          return (
            <div
              key={layout}
              className={styles.previewColumn}
              data-mobile={isMobile}
              data-active={isActive}
              data-testid={`form-fields-preview-${layout}`}
              onClick={() => _selectActiveLayout(layout)}
            >
              <div className={styles.previewHeader}>
                <Button
                  variant="minimal"
                  active={isActive}
                  iconPrefix={isMobile ? "mobileDevice" : "desktopDevice"}
                  className={styles.previewLabel}
                >
                  <Text variant="body-2" className={styles.previewLabelText}>
                    {layout.toUpperCase()} PREVIEW
                  </Text>
                </Button>
                {!isMobile && (
                  <SegmentedTabs
                    options={PREVIEW_BACKGROUNDS}
                    value={previewBackground}
                    onChange={setPreviewBackground}
                    idPrefix="form-fields-preview-background-"
                    className={styles.backgroundTabs}
                    // Picking a background shouldn't also switch the active layout.
                    onClick={(event) => event.stopPropagation()}
                  />
                )}
              </div>
              <div
                className={isMobile ? styles.previewMobile : styles.previewDesktop}
                data-background={previewBackground}
              >
                <DesignSettingsFormFieldsPreview
                  theme={theme}
                  layout={layout}
                  fieldState={fieldState}
                  onSelectField={_selectFieldSection}
                  designContext={designContext}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DesignSettingsFormFields;
